/* /ess — employee self-service home (prototype pages3.js pgEss). */
import { Link, useNavigate } from "react-router-dom";
import { useEmployee, useLeaveBalance, useLeaves, useLetters, usePayslip, useToday } from "../../data/hooks";
import { daysTo } from "../../domain/dates";
import { useI18n } from "../../i18n";
import { gDate, hDate, MONTHS_AR, MONTHS_EN } from "../../lib/format";
import { Alert, Avatar, Bar, Card, Chip, EmptyState, Icon, Loaded, Money } from "../../ui";
import { CheckButton } from "./CheckButton";
import { nextPayDate, periodOf, shiftPeriod, useEmployeeId } from "./essLib";

function NoLink() {
  const { L } = useI18n();
  return <EmptyState title={L("Your account isn't linked to an employee record", "حسابك غير مرتبط بسجل موظف")} desc={L("Ask HR to link your user to your employee profile.", "اطلب من الموارد البشرية ربط حسابك بملفك الوظيفي.")} />;
}
export { NoLink };

function Home({ empId }: { empId: number }) {
  const { t, L, lang, name } = useI18n();
  const nav = useNavigate();
  const today = useToday();
  const emp = useEmployee(empId);
  const bal = useLeaveBalance(empId);
  const lastPeriod = shiftPeriod(periodOf(today), -1);
  const slip = usePayslip(empId, lastPeriod);
  const leaves = useLeaves();
  const letters = useLetters();
  const pay = nextPayDate(today);
  const dleft = daysTo(today, pay);
  const lm = Number(lastPeriod.slice(5)) - 1;
  return (
    <Loaded q={emp}>{(e) => {
      const iq = e.iqamaExpiry ? daysTo(today, e.iqamaExpiry) : null;
      const myLeaves = (leaves.data || []).filter((l) => l.employeeId === empId);
      const myLetters = (letters.data || []).filter((l) => l.employeeId === empId);
      return (
        <div className="ess stack lg" data-testid="ess-home">
          <div>
            <div className="sm muted">{gDate(today, lang, { weekday: "long", day: "numeric", month: "long" })} · <span dir="rtl">{hDate(today)}</span></div>
            <h1 style={{ fontSize: "var(--fs-h1)", marginTop: 4 }}>{L("Hi ", "أهلًا ")}{name(e).split(" ")[0]}</h1>
          </div>
          <section className="ess-hero">
            <div className="row between">
              <div>
                <div className="sm muted">{t("Next payday")}</div>
                <div className="row nw" style={{ alignItems: "baseline", gap: 8 }}><span className="count" data-testid="payday-count">{dleft}</span><span className="muted">{t("days")} · {gDate(pay, lang)}</span></div>
              </div>
              <Avatar name={e.nameEn} size="lg" />
            </div>
            <CheckButton employeeId={empId} today={today} />
          </section>
          <div className="g g-2">
            <button type="button" className="card ess-tile" onClick={() => nav("/ess/leave")} data-testid="tile-leave">
              <span className="kpi-ico info"><Icon name="plane" /></span>
              <span className="sm muted">{t("Leave balance")}</span>
              <span className="kpi-v" data-testid="leave-remaining">{bal.data ? bal.data.remaining : "—"}<span className="kpi-u">{t("days")}</span></span>
              {bal.data && <Bar value={bal.data.used} max={bal.data.entitlement} tone="info" />}
            </button>
            <button type="button" className="card ess-tile" onClick={() => nav("/payslip/" + empId + "/" + lastPeriod)} data-testid="tile-payslip">
              <span className="kpi-ico gold"><Icon name="receipt" /></span>
              <span className="sm muted">{L("Last payslip", "آخر قسيمة")} · {L(MONTHS_EN[lm], MONTHS_AR[lm])}</span>
              <span className="kpi-v" style={{ fontSize: 22 }} data-testid="last-net">{slip.data ? <Money value={slip.data.net} /> : "—"}</span>
            </button>
          </div>
          <Card title={t("Quick actions")}>
            <div className="qa">
              <Link className="qa-btn" to="/ess/leave"><span className="kpi-ico"><Icon name="plane" /></span>{t("Request leave")}</Link>
              <Link className="qa-btn" to="/ess/letters"><span className="kpi-ico gold"><Icon name="mail" /></span>{t("Request letter")}</Link>
              <Link className="qa-btn" to="/ess/docs"><span className="kpi-ico warn"><Icon name="id-card" /></span>{t("My documents")}</Link>
              <Link className="qa-btn" to="/ess/attendance"><span className="kpi-ico info"><Icon name="clock" /></span>{L("My attendance", "حضوري")}</Link>
            </div>
          </Card>
          {iq != null && iq <= 60 && (
            <Alert tone={iq < 0 ? "bad" : "warn"} testId="iqama-alert"
              title={iq < 0 ? L(`Your Iqama expired ${-iq} days ago`, `انتهت إقامتك منذ ${-iq} يومًا`) : L(`Your Iqama expires in ${iq} days`, `تنتهي إقامتك خلال ${iq} يومًا`)}
              desc={L("HR has started the renewal. No action needed from you.", "بدأت الموارد البشرية التجديد، لا يلزمك أي إجراء.")} />
          )}
          <Card title={L("My requests", "طلباتي")} testId="my-requests">
            {myLeaves.length + myLetters.length === 0 ? <p className="muted sm" style={{ margin: 0 }}>{L("No requests yet.", "لا توجد طلبات بعد.")}</p> : null}
            {myLeaves.map((l) => (
              <div key={"l" + l.id} className="list-row"><span className="kpi-ico"><Icon name="plane" /></span><div className="grow"><div>{t(l.type)} · {l.days} {t("days")}</div><div className="xs muted">{gDate(l.from, lang)} → {gDate(l.to, lang)}</div></div><Chip status={l.status} /></div>
            ))}
            {myLetters.map((l) => (
              <div key={"t" + l.id} className="list-row"><span className="kpi-ico gold"><Icon name="mail" /></span><div className="grow"><div>{t(l.kind)}</div><div className="xs muted">{l.to}</div></div><Chip status={l.status} /></div>
            ))}
          </Card>
        </div>
      );
    }}</Loaded>
  );
}

export default function EssHome() {
  const id = useEmployeeId();
  return id == null ? <NoLink /> : <Home empId={id} />;
}
